import { keyframes } from '@emotion/react';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import React from 'react';
import PropTypes from 'prop-types';

import { useAppContext } from '../hooks/AppProvider';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const riseAndFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translate3d(0, 24px, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
`;

const useEnhancedEffect = typeof window === 'undefined' ? React.useEffect : React.useLayoutEffect;

const mergeSx = (baseSx, nextSx) => {
  if (!baseSx) {
    return nextSx;
  }

  if (!nextSx) {
    return baseSx;
  }

  return Array.isArray(baseSx) ? [...baseSx, nextSx] : [baseSx, nextSx];
};

const StyledReveal = styled(Box, {
  shouldForwardProp: (prop) => !['delay', 'isVisible', 'initiallyVisible', 'reducedMotion', 'liftOnReveal'].includes(prop),
})(({ delay, initiallyVisible, isVisible, reducedMotion, liftOnReveal }) => ({
  opacity: reducedMotion || isVisible ? 1 : 0,
  transform: reducedMotion || isVisible ? 'none' : `translate3d(0, ${liftOnReveal ? 24 : 0}px, 0)`,
  willChange: reducedMotion ? 'auto' : 'opacity, transform',
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    opacity: 1,
    transform: 'none',
    willChange: 'auto',
  },
  ...(reducedMotion || !isVisible ? {} : {
    animation: initiallyVisible
      ? `${fadeIn} 520ms ease-out ${delay}ms both`
      : `${riseAndFadeIn} 700ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms both`,
  }),
}));

const Reveal = ({
  children = null,
  asChild = false,
  delay = 0,
  threshold = 0.15,
  rootMargin = '0px 0px -10% 0px',
  liftOnReveal = true,
}) => {
  const { reducedMotion } = useAppContext();
  const ref = React.useRef(null);
  const [isVisible, setIsVisible] = React.useState(reducedMotion);
  const [initiallyVisible, setInitiallyVisible] = React.useState(false);

  useEnhancedEffect(() => {
    if (reducedMotion || !ref.current) {
      return undefined;
    }

    const element = ref.current;
    const rect = element.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const inViewport = rect.bottom > 0 && rect.right > 0 && rect.top < viewportHeight && rect.left < viewportWidth;

    if (inViewport) {
      setInitiallyVisible(true);
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        setInitiallyVisible(false);
        setIsVisible(true);
        observer.disconnect();
      });
    }, {
      rootMargin,
      threshold,
    });

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [reducedMotion, rootMargin, threshold]);

  const revealProps = {
    delay,
    initiallyVisible,
    isVisible,
    reducedMotion,
    liftOnReveal,
  };

  if (asChild && React.isValidElement(children)) {
    const childSx = mergeSx(children.props.sx, {
      opacity: reducedMotion || isVisible ? 1 : 0,
      transform: reducedMotion || isVisible ? 'none' : `translate3d(0, ${liftOnReveal ? 24 : 0}px, 0)`,
      willChange: reducedMotion ? 'auto' : 'opacity, transform',
      ...(reducedMotion || !isVisible ? {} : {
        animation: initiallyVisible
          ? `${fadeIn} 520ms ease-out ${delay}ms both`
          : `${riseAndFadeIn} 700ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms both`,
      }),
    });

    return React.cloneElement(children, {
      ref,
      sx: childSx,
      'data-reveal-visible': isVisible || reducedMotion ? 'true' : 'false',
      'data-reveal-initially-visible': initiallyVisible ? 'true' : 'false',
    });
  }

  return (
    <StyledReveal
      ref={ref}
      {...revealProps}
    >
      {children}
    </StyledReveal>
  );
};

Reveal.propTypes = {
  children: PropTypes.node,
  asChild: PropTypes.bool,
  delay: PropTypes.number,
  threshold: PropTypes.number,
  rootMargin: PropTypes.string,
  liftOnReveal: PropTypes.bool,
};

export default Reveal;